import * as actions_admin from 'actions/admin-actions';
import * as actions_assets from 'actions/assets-actions';
import * as actions_service_requests from 'actions/service-requests-actions';
import * as toolbox from 'components/common/toolbox';
import ActivityLog from 'components/activity-log/activity-log';
import Authorize from 'components/common/authorize';
import EllipsisDropdown from 'components/common/ellipsis-dropdown';
import ExpandDetails from 'components/common/expand-details';
import React, { useEffect, useRef, useState } from 'react';
import SpeedDialButton from 'components/common/speed-dial-button'; 
import { APPS, ASSETS, ADDITIONAL_FIELDS, LOG, PERMISSIONS } from 'components/constants';
import { Avatar, CloseX, DashboardDetail, ModalForm, Spinner } from 'enspire-manager-framework';
import { imageSize, numPanels } from 'components/common/toolbox';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

const _ = require('lodash');

export default function AssetDashboard(props) {

	/* Hooks --------------------------*/

	const seg = 2;
	const params = useParams();
	const history = useHistory();
	const dispatch = useDispatch();
	const expandRef = useRef();    
	const routeLocation = useLocation();
    const pathname = routeLocation.pathname;

	const admin = useSelector((store) => store.admin);
	const assetTypes = useSelector((store) => store.settings.settings_asset_types);
	const assets = useSelector((store) => store.assets);
	const features = useSelector((store) => store.settings?.settings?.features)?.[(params.appId == APPS.DISPATCH.id) ? 'dispatch' : 'assets'];
	const inventory = useSelector((store) => store.inventory);
	const services = useSelector((store) => store.serviceRequests);
	const settings = useSelector((store) => store.settings);
	const user_permissions = useSelector(store => store.users?.user_permissions);

	const asset = assets.asset;
	const assetType = assetTypes.find((assetType) => assetType.id == asset?.assetTypeId);
	const assetStatus = _.find(Object.values(ASSETS), { id: asset?.statusId ?? '1' });
	const segments = routeLocation.pathname.split('/').length;
    const segmentsClosed = (segments > 8);

	const [showActivityModal, setShowActivityModal] = useState(false);

	/* Effects --------------------------*/

	useEffect(() => {
		if (parseInt(params.asset_id)) dispatch(actions_assets.subAsset(params.handle, params.asset_id));
		return () => {
			let unsubscribe = assets.asset_unsubscribe;
			if (typeof unsubscribe === "function") unsubscribe();
		};
	}, [params.asset_id]);
	
	useEffect(() => {
		// dispatch(actions_inventory.subInventoryByAssetId(params.handle, params.asset_id));
		dispatch(actions_service_requests.subAssetServiceRequests(params.handle, params.asset_id));
		return () => {
			const unsubscribe1 = inventory.inventory_unsubscribe;
			if (typeof unsubscribe1 == 'function') unsubscribe1();
			const unsubscribe2 = services.service_requests_unsubscribe;
			if (typeof unsubscribe2 == 'function') unsubscribe2();
		};
	}, [params.asset_id]);

	// Trigger lightbox when URL param changes ------------------------------------------------------------------
	useEffect(() => {
		const imageIndex = parseInt(params.form_id);

		if (params.form == 'lightbox') {
			const images = asset.images?.map((image) => image.imageUrl.includes('.pdf')
				?   { type: 'custom', content: toolbox.getEmbedCode(image.imageUrl), title: asset.name, description: image.caption }
				:   { src: imageSize(image.imageUrl, 'lg'), title: asset.name, description: image.caption }
			);
			dispatch(actions_admin.lightboxSources([ ...images ]));
			dispatch(actions_admin.lightboxIndex(imageIndex));
			dispatch(actions_admin.lightboxShow(true));
        } else if (params.form == 'form'){
			dispatch(actions_admin.lightboxShow(false));
		}
	}, [params.form, params.form_id]);

	useEffect(() => {
		(segmentsClosed) ? expandRef.current?.setClosed() : expandRef.current?.setOpen();
	}, [segmentsClosed])
	
	/* Actions --------------------------*/
	
	const handleSpeedDial = (value, action) => {
		if (value == 'quote') return;
		openServiceForm(value);
	}
	const openServiceForm = (type) => {
		let location = toolbox.modifyPath(props.location.pathname, seg+3, type, 13);
		props.history.push({ pathname: location });
	}
	const openForm = () => {
		let location = toolbox.modifyPath(pathname, seg+3, "asset-form/" + params.asset_id);
		history.push({ pathname: location });
	};
	const toggleLightbox = () => {
		let location = toolbox.modifyPath(pathname, seg+3, "lightbox/0");
		history.push({ pathname: location });
	}

	/* Constants --------------------------*/

	const additionalFields = (Object.values(ADDITIONAL_FIELDS).find((field) => field.id == assetType?.additionalFieldsId)?.columns)?.flat();
	const searchFields = (Object.values(ADDITIONAL_FIELDS).find((field) => field.id == assetType?.additionalFieldsId)?.search)?.flat();
	const additionalDetails = additionalFields?.map((detail) => <DashboardDetail key={detail.name} field={ detail.name } value={ asset?.[detail.field] } />);
	const searchDetails = searchFields?.map((detail) => <DashboardDetail key={detail.name} field={ detail.name } value={ asset?.[detail.field] } />);
	const classification = _.find(settings.settings_asset_classifications, { id: asset?.classificationId });

	const restrictedBadges = asset?.permittedRoles?.map((role) => (
		<span key={role.id} className={ 'mr-2 mb-3 badge badge-info' }>
			<i className="fa-solid fa-lock mr-1"></i> {role.label}
		</span>
	));

	return (
		<div className="row">
			{asset && parseInt(params.asset_id)
				? <>
					{ !window.flutter && segments <= seg+6 &&
						<CloseX onClick={() => { history.goBack(); }} className="mr-2 mt-n1" color={ '#aaaaaa' } />
					}
					<div id="asset-avatar" className={ 'col-12 pt-2 dashboard-avatar' }>
						<div className="profile-image" onClick={ (!!asset.images?.length) ? toggleLightbox : null }>
							{ !!asset.images?.length &&
								<Avatar
									style={{ position: 'absolute', left: '70px', top: '55px' }}
									color="white"
									bgColor="black"
									size={35}
									fontSize={ '10px' }
									name={`+${asset.images.length}`}
									initials={ false }
									border="3px solid white"
									role="button"
								/>
							}
							<Avatar className="mx-auto"
								color="white"
								bgColor="saddleBrown"
								size={95}
								name={asset.name}
								image={imageSize(asset.images?.[0]?.imageUrl ?? '', 'sm')}
								border="4px solid white"
								role={ (asset.images?.length) ? "button" : null }
							/>
						</div>
						<div className="profile-info d-flex justify-content-center flex-column align-items-start" style={{ height: '90px' }}>
							<h1 className="m-0">{asset.name}</h1>
								<div>
									<span className="mr-3">{`#${asset.unitNumber}`}</span>
									<span className={ 'mr-2 badge badge-secondary' }>{classification?.name}</span>
								</div>
								{ restrictedBadges }
						</div>
					</div>

					{/* EXPANDABLE DETAILS ------------------------------------------------------ */}

					<div className="col-12">
						<div id="asset-details" className="mb-2">
							<ExpandDetails default={(segmentsClosed) ? 'closed' : 'open'} top={ -92 } noControls={ !segmentsClosed } noEllipsis ref={expandRef}>
								<div className="d-flex align-items-center position-relative">
									<button type="button" 
										className={'btn btn-default btn-sm mr-2' }
										disabled={!user_permissions[PERMISSIONS.ASSETS_EDIT.key]}
										onClick={openForm}><i className="fa-regular fa-gear fa-lg text-muted mr-1"></i>Edit Asset
									</button>

									<button type="button" className="mr-2 btn btn-default btn-sm" style={{ cursor: 'default' }}>
										{ assetStatus.icon }
										{ assetStatus?.name } 
									</button>

									<EllipsisDropdown actions={[
										...(user_permissions[PERMISSIONS.ACTIVITY_LOGS_VIEW.key]) ? [{ label: 'Activity Log', onClick: () => setShowActivityModal(true) }] : [],
									]} />
								</div>

								<div id="asset-details" className="mt-3 mb-2">
									<DashboardDetail field={ 'Unit #' } value={ asset.unitNumber } />
									{ additionalDetails }
									{ searchDetails }
									<DashboardDetail field={ 'Description' } value={ asset.description } type="text" />
								</div>
							</ExpandDetails>
						</div>
					</div>
                    
					<Authorize appId={ APPS.ASSETS.id } permKeys={[PERMISSIONS.QUOTES_CREATE.key, PERMISSIONS.SERVICE_REQUESTS_CREATE.key]} and={!admin.bottom_sheet_show && params.form == 'form'}>
						<SpeedDialButton 
							columnRef={ props.columnRef }
							onClick={handleSpeedDial}
							actions={[
								...(features['generate_quotes'] && user_permissions[PERMISSIONS.QUOTES_CREATE.key]) ? [{
									name: '+ Quote', 
									value: 'quote', 
									className: '', 
									icon: <i className="fa-solid fa-comment-plus fa-xl"></i>
								}] : [],
								...(user_permissions[PERMISSIONS.SERVICE_REQUESTS_CREATE.key]) ? [{
									name: '+ Service Record', 
									value: 'record-form', 
									className: '', 
									icon: <i className="fa-solid fa-receipt fa-xl"></i>
								}] : [],
								...(user_permissions[PERMISSIONS.SERVICE_REQUESTS_CREATE.key]) ? [{
									name: '+ Service Request', 
									value: 'request-form', 
									className: '', 
									icon: <i className="fa-solid fa-folder-plus fa-xl"></i>
								}] : [],
							]}
						/>
					</Authorize>

					{showActivityModal && (
						<ModalForm
							modal_header="Asset Activity"
							cancel_button_title="Close"
							cancel_button_callback={() => setShowActivityModal(false)}
							hide_submit={ true }
							no_padding={ true }
							size="md"
						>
							<ActivityLog 
								id="asset_id" 
								prefix="ASSET" 
								singular="asset"
								store="assets" 
								type={ LOG.ASSET.key } 
							/>
						</ModalForm>
					)}
				</>
				: assets.asset_selected_pending 
					? <Spinner center />
					: <div className="col-12"><h2 className="text-center mb-4">Asset Unavailable</h2></div>
			}
		</div>
	);
}
